'use client';

/**
 * Small pieces the setup steps share. Kept here so every step reads the same way: what we are
 * checking, what the printer said, and what was concluded — in that order, always visible.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, MinusCircle, Loader2 } from 'lucide-react';
import { cn } from './shims';
import type { CheckRecord, CheckStatus } from '../../../lib/masung-printer/profile';
import type { WireEntry } from '../../../lib/masung-printer/printer';
import type { TraceSample, TraceAnalysis } from '../../../lib/masung-printer/calibration';

export function StatusIcon({ status, className }: { status?: CheckStatus | 'busy'; className?: string }) {
  const c = cn('h-4 w-4 shrink-0', className);
  if (status === 'busy') return <Loader2 className={cn(c, 'animate-spin text-muted-foreground')} />;
  if (status === 'pass') return <CheckCircle2 className={cn(c, 'text-green-600')} />;
  if (status === 'warn') return <AlertTriangle className={cn(c, 'text-amber-600')} />;
  if (status === 'fail') return <XCircle className={cn(c, 'text-red-600')} />;
  if (status === 'skipped') return <MinusCircle className={cn(c, 'text-muted-foreground')} />;
  return <span className={cn(c, 'rounded-full border-2 border-muted-foreground/40')} />;
}

/** The verdict box at the foot of a step — what is now on record, and the evidence for it. */
export function Verdict({ check }: { check?: CheckRecord }) {
  if (!check) return null;
  const tone = {
    pass: 'border-green-600/30 bg-green-600/5',
    warn: 'border-amber-500/40 bg-amber-500/10',
    fail: 'border-red-600/30 bg-red-600/5',
    skipped: 'border-border bg-muted/40',
  }[check.status];
  return (
    <div className={cn('mt-4 flex gap-2 rounded-lg border px-3 py-2 text-[13px]', tone)}>
      <StatusIcon status={check.status} className="mt-0.5" />
      <div>
        <div className="font-medium">{{ pass: 'Confirmed', warn: 'Works, with a caveat', fail: 'Not working yet', skipped: 'Skipped' }[check.status]}</div>
        <div className="text-muted-foreground whitespace-pre-wrap">{check.evidence}</div>
      </div>
    </div>
  );
}

export function StepHeader({ title, why }: { title: string; why: ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="text-[17px] font-bold tracking-[-0.2px]">{title}</h3>
      <p className="mt-1 text-[13px] text-muted-foreground max-w-prose">{why}</p>
    </div>
  );
}

/** Large touch-friendly single choice. */
export function Choice<T extends string>({ value, onChange, options }: {
  value: T | undefined;
  onChange: (v: T) => void;
  options: { value: T; label: string; hint?: string }[];
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-lg border px-3 py-2.5 text-left transition-colors',
            value === o.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/60',
          )}
        >
          <div className="text-[13px] font-medium">{o.label}</div>
          {o.hint && <div className="text-[12px] text-muted-foreground">{o.hint}</div>}
        </button>
      ))}
    </div>
  );
}

export function KV({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <div className="rounded-lg border divide-y text-[13px]">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 px-3 py-1.5">
          <span className="text-muted-foreground">{k}</span>
          <span className="font-mono text-right break-all">{v === '' || v === undefined || v === null ? <span className="text-muted-foreground">(no answer)</span> : v}</span>
        </div>
      ))}
    </div>
  );
}

export function Note({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'error'; children: ReactNode }) {
  return (
    <div className={cn('rounded-lg border px-3 py-2 text-[13px]', {
      info: 'border-border bg-muted/40',
      warn: 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200',
      error: 'border-red-600/30 bg-red-600/5 text-red-800 dark:text-red-300',
    }[tone])}>{children}</div>
  );
}

/** Re-render on an interval — the wire log and sim paper are plain arrays on the driver. */
export function useTick(ms = 400) {
  const [, set] = useState(0);
  useEffect(() => { const id = setInterval(() => set((n) => n + 1), ms); return () => clearInterval(id); }, [ms]);
}

export function WireLog({ entries }: { entries: WireEntry[] }) {
  const shown = entries.slice(-80);
  const box = useRef<HTMLDivElement>(null);
  const last = entries[entries.length - 1]?.t;
  // Follow the newest bytes — the point of the log is what just happened.
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [last, entries.length]);
  return (
    <div ref={box} className="h-full overflow-y-auto font-mono text-[11px] leading-[1.45] px-3 py-2">
      {shown.length === 0 && <div className="text-muted-foreground">Nothing sent yet.</div>}
      {shown.map((e, i) => (
        <div key={i} className={cn(e.dir === 'tx' ? 'text-sky-700 dark:text-sky-400' : e.dir === 'rx' ? 'text-green-700 dark:text-green-400 font-semibold' : 'text-muted-foreground italic')}>
          <span className="text-muted-foreground">{new Date(e.t).toLocaleTimeString()}</span>{' '}
          {e.dir === 'tx' ? '→' : e.dir === 'rx' ? '←' : '·'} {e.hex}
        </div>
      ))}
    </div>
  );
}

/** The paper as the sensor saw it: AD reading against millimetres of travel. */
export function TraceChart({ samples, analysis }: { samples: TraceSample[]; analysis?: TraceAnalysis | null }) {
  const W = 640, H = 140, pad = 24;
  const maxMm = Math.max(50, ...samples.map((s) => s.mm));
  const x = (mm: number) => pad + (mm / maxMm) * (W - pad * 2);
  const y = (ad: number) => H - pad - (ad / 255) * (H - pad * 2);
  const pts = samples.map((s) => `${x(s.mm).toFixed(1)},${y(s.ad).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-lg border bg-card" role="img" aria-label="Sensor reading along the paper">
      {analysis?.marks.map((m, i) => (
        <rect key={i} x={x(m.startMm)} y={pad / 2} width={Math.max(2, x(m.endMm) - x(m.startMm))} height={H - pad * 1.5} className="fill-primary/15" />
      ))}
      {analysis && analysis.verdict !== 'no-readings' && (
        <line x1={pad} x2={W - pad} y1={y(analysis.threshold)} y2={y(analysis.threshold)} className="stroke-amber-500" strokeDasharray="4 3" />
      )}
      <polyline points={pts} fill="none" className="stroke-foreground" strokeWidth={1.5} />
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <text key={f} x={x(maxMm * f)} y={H - 6} textAnchor="middle" className="fill-muted-foreground text-[10px]">{Math.round(maxMm * f)} mm</text>
      ))}
      <text x={4} y={y(255) + 4} className="fill-muted-foreground text-[10px]">255</text>
      <text x={4} y={y(0)} className="fill-muted-foreground text-[10px]">0</text>
    </svg>
  );
}

export function NumberField({ label, value, onChange, suffix, placeholder }: {
  label: string; value: number | undefined; onChange: (v: number | undefined) => void; suffix?: string; placeholder?: string;
}) {
  return (
    <label className="flex items-center gap-2 text-[13px]">
      <span className="w-56 text-muted-foreground">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        className="h-9 w-28 rounded-md border bg-background px-2 font-mono"
      />
      {suffix && <span className="text-muted-foreground">{suffix}</span>}
    </label>
  );
}
